import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { parsePhaseGuide } from "@/lib/schema/phaseGuide";
import { addDays } from "@/lib/advice/dates";
import {
  defaultLeadTimeDays,
  matchProcurementItems
} from "@/lib/advice/procurementMatch";
import type { Insert, Tables, Update } from "@/types/database";
import { qk } from "./_queryKeys";
import { assertData, throwIfError } from "./_supabaseErrors";

export type ProcurementItem = Tables<"procurement_items">;
export type ProcurementItemInsert = Insert<"procurement_items">;
export type ProcurementItemUpdate = Update<"procurement_items">;

export async function listProcurementItems(): Promise<ProcurementItem[]> {
  const { data, error } = await supabase
    .from("procurement_items")
    .select("*")
    .order("needed_by", { ascending: true, nullsFirst: false })
    .order("name", { ascending: true });
  throwIfError(error);
  return data ?? [];
}

export async function listProcurementItemsByPhase(phaseId: string): Promise<ProcurementItem[]> {
  const { data, error } = await supabase
    .from("procurement_items")
    .select("*")
    .eq("phase_id", phaseId)
    .order("name", { ascending: true });
  throwIfError(error);
  return data ?? [];
}

export async function createProcurementItem(
  values: ProcurementItemInsert
): Promise<ProcurementItem> {
  const { data, error } = await supabase
    .from("procurement_items")
    .insert(values)
    .select("*")
    .single();
  throwIfError(error);
  return assertData(data);
}

export async function updateProcurementItem(
  id: string,
  values: ProcurementItemUpdate
): Promise<ProcurementItem> {
  const { data, error } = await supabase
    .from("procurement_items")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  throwIfError(error);
  return assertData(data, "A beszerzési tétel nem található.");
}

export async function deleteProcurementItem(id: string): Promise<void> {
  const { error } = await supabase.from("procurement_items").delete().eq("id", id);
  throwIfError(error);
}

/**
 * A listát nem kézzel gépeljük: a fázis-útmutatók anyagtételeiből generáljuk.
 * A fázison belüli név-egyediség miatt ismételhető — csak az újakat szúrja be.
 */
export async function syncProcurementFromGuides(options?: {
  phaseIds?: string[];
}): Promise<{ inserted: number }> {
  const phasesQuery = supabase.from("phases").select("id,title,guide");
  const { data: phases, error } = options?.phaseIds?.length
    ? await phasesQuery.in("id", options.phaseIds)
    : await phasesQuery;
  throwIfError(error);

  const { data: existing, error: exErr } = await supabase
    .from("procurement_items")
    .select("phase_id,name");
  throwIfError(exErr);

  const have = new Set(
    (existing ?? []).map((r) => `${r.phase_id ?? ""}:${r.name.trim().toLowerCase()}`)
  );

  const rows: ProcurementItemInsert[] = [];
  for (const phase of phases ?? []) {
    const guide = parsePhaseGuide(phase.guide);
    for (const item of guide.equipment.items) {
      const name = item.name.trim();
      if (!name) continue;
      // Csak beszerzendő anyag; a kéziszerszám és a védőeszköz nem tartozik ide.
      if (item.category && item.category !== "material") continue;
      const key = `${phase.id}:${name.toLowerCase()}`;
      if (have.has(key)) continue;
      have.add(key);
      const leadTime = defaultLeadTimeDays(name);
      rows.push({
        phase_id: phase.id,
        name,
        category: "material",
        status: "needed",
        source: "guide",
        ...(item.note ? { quantity: item.note } : {}),
        ...(leadTime === null ? {} : { lead_time_days: leadTime })
      });
    }
  }

  if (rows.length === 0) return { inserted: 0 };

  const { error: insErr } = await supabase.from("procurement_items").insert(rows);
  throwIfError(insErr);
  return { inserted: rows.length };
}

/**
 * `needed_by = feladat vagy fázis tervezett kezdése − átfutási idő`.
 * Ettől lesz a listából figyelmeztetés: „ezt a jövő héten meg kell rendelni".
 */
export async function recomputeProcurementNeededBy(): Promise<{ updated: number }> {
  const [{ data: items, error: iErr }, { data: tasks, error: tErr }, { data: phases, error: pErr }] =
    await Promise.all([
      supabase.from("procurement_items").select("*"),
      supabase.from("tasks").select("id,phase_id,order_index,date_start_planned"),
      supabase.from("phases").select("id,date_start_planned")
    ]);
  throwIfError(iErr);
  throwIfError(tErr);
  throwIfError(pErr);

  const taskStart = new Map((tasks ?? []).map((t) => [t.id, t.date_start_planned]));
  const phaseStart = new Map((phases ?? []).map((p) => [p.id, p.date_start_planned]));
  const earliestTaskStartByPhase = new Map<string, string>();
  for (const t of tasks ?? []) {
    if (!t.phase_id || !t.date_start_planned) continue;
    const current = earliestTaskStartByPhase.get(t.phase_id);
    if (!current || t.date_start_planned < current) {
      earliestTaskStartByPhase.set(t.phase_id, t.date_start_planned);
    }
  }

  let updated = 0;
  for (const item of items ?? []) {
    const lead = item.lead_time_days;
    if (!lead) continue;
    const buffer = item.order_buffer_days ?? 3;
    const anchor =
      (item.task_id ? taskStart.get(item.task_id) : null) ??
      (item.phase_id ? earliestTaskStartByPhase.get(item.phase_id) : null) ??
      (item.phase_id ? phaseStart.get(item.phase_id) : null) ??
      null;
    if (!anchor) continue;
    // F3.5: latest_order_date = feladat kezdete − átfutás − puffer
    const latestOrder = addDays(anchor, -(lead + buffer));
    if (latestOrder === item.latest_order_date && latestOrder === item.needed_by) continue;
    await updateProcurementItem(item.id, {
      latest_order_date: latestOrder,
      needed_by: latestOrder
    });
    updated += 1;
  }
  return { updated };
}

/** Nyitott tételek közelgő / lejárt rendelési határideje (Gantt + riasztás). */
export type OrderDeadlineMarker = {
  id: string;
  name: string;
  phase_id: string | null;
  latest_order_date: string;
  overdue: boolean;
};

export async function listUpcomingOrderDeadlines(today: string, withinDays = 45): Promise<OrderDeadlineMarker[]> {
  const { data, error } = await supabase
    .from("procurement_items")
    .select("id, name, phase_id, latest_order_date, needed_by, status")
    .not("status", "in", "(delivered,not_needed,ordered)");
  throwIfError(error);

  const horizon = addDays(today, withinDays);
  const out: OrderDeadlineMarker[] = [];
  for (const item of data ?? []) {
    const d = item.latest_order_date ?? item.needed_by;
    if (!d) continue;
    if (d > horizon) continue;
    out.push({
      id: item.id,
      name: item.name,
      phase_id: item.phase_id,
      latest_order_date: d,
      overdue: d < today
    });
  }
  return out.sort((a, b) => a.latest_order_date.localeCompare(b.latest_order_date));
}

export const upcomingOrderDeadlinesQueryOptions = (today: string) =>
  queryOptions({
    queryKey: [...qk.procurement, "orderDeadlines", today] as const,
    queryFn: () => listUpcomingOrderDeadlines(today)
  });

export type ProcurementMatchResult = {
  item: ProcurementItem;
  score: number;
};

/**
 * Kiadás → beszerzési tétel párosítás. Konzervatív: csak az adott fázis /
 * feladat nyitott tételei közül választ, és csak magabiztos találatra jelöl.
 */
export async function matchExpenseToProcurement(expense: {
  id: string;
  title: string;
  phase_id: string | null;
  task_id: string | null;
}): Promise<ProcurementMatchResult | null> {
  let query = supabase.from("procurement_items").select("*");
  if (expense.phase_id) query = query.eq("phase_id", expense.phase_id);
  else if (expense.task_id) query = query.eq("task_id", expense.task_id);
  else return null;

  const { data: items, error } = await query;
  throwIfError(error);
  const candidates = items ?? [];
  if (candidates.length === 0) return null;

  const matches = matchProcurementItems(expense.title, candidates);
  const best = matches[0];
  if (!best) return null;
  const item = candidates.find((c) => c.id === best.id);
  if (!item) return null;

  const updated = await updateProcurementItem(item.id, {
    status: "delivered",
    expense_id: expense.id
  });
  return { item: updated, score: best.score };
}

export const procurementItemsQueryOptions = () =>
  queryOptions({
    queryKey: qk.procurement,
    queryFn: listProcurementItems
  });

export const procurementItemsByPhaseQueryOptions = (phaseId: string) =>
  queryOptions({
    queryKey: [...qk.procurement, { phaseId }] as const,
    queryFn: () => listProcurementItemsByPhase(phaseId)
  });
